import copyArray from "../../../infrastructure/helpers/common/copyArray";
import copyObject from "../../../infrastructure/helpers/common/copyObject";
import httpClient from "../../../infrastructure/helpers/common/httpClient";
import urlHelper from "../../../infrastructure/helpers/common/urlHelper";
import reportConfigAdapter from "../../../infrastructure/helpers/report/import/reportConfigAdapter";
import ColumnDefinition from "../../../infrastructure/types/DataGrid/ColumnDefinition";
import Lookup from "../../../types/functions/Lookup";
import IngestionConfig from "../../../types/ingestion/IngestionConfig";
import ComponentType from "../../../types/report/ComponentType";
import GlobalModuleConfig from "../../../types/report/GlobalModuleConfig";
import IdNameAndNumber from "../../../types/report/IdNameAndNumber";
import Mode from "../../../types/report/Mode";
import ReportConfig from "../../../types/report/ReportConfig";
import ReportVariable from "../../../types/report/ReportVariable";
import UserDefinedFunction from "../../../types/report/UserDefinedFunction";
import IdMaps from "../../../types/report/import/IdMaps";
import NameAndError from "../../../types/report/import/NameAndError";
import SwitchComponent from "../../Common/Switch/Switch";
import ComponentInPopup from "./Types/ComponentInPopup";
import GlobalModuleComponentsAnalysisResult from "./Types/GlobalModuleComponentsAnalysisResult";
import MissingAndExistingComponents from "./Types/MissingAndExistingComponents";
import React from "react";

const updateLookupsDataSources = (lookups: Lookup[], maps: IdMaps): void => {

    lookups.forEach((lookup) => {
        reportConfigAdapter.adaptLookup(lookup, maps);
    });
}

const findUsedGlobalModuleDataSources = (reportConfig: ReportConfig, globalModuleDataSources: IngestionConfig[], reportConfigDataSourcesNames: string[]): IngestionConfig[] => {

    let result = [] as IngestionConfig[];

    reportConfig.lookups.filter(x => x.mode === Mode.Global).forEach((l) => {

        let ds1 = globalModuleDataSources.find(x => x.id === l.dataSource1Id) as IngestionConfig;

        if (!reportConfigDataSourcesNames.includes(ds1?.name as string)) {
            result.push(ds1);
        }

        let ds2 = globalModuleDataSources.find(x => x.id === l.dataSource2Id) as IngestionConfig;

        if (!reportConfigDataSourcesNames.includes(ds2?.name as string)) {
            result.push(ds2);
        }
    });

    return result;
}

const analyseGlobalModuleComponents = (currentGlobalModule: GlobalModuleConfig, reportConfig: ReportConfig): GlobalModuleComponentsAnalysisResult => {

    let lookups = Object.keys(currentGlobalModule).length > 0 ?
        getComponents(copyArray(reportConfig.lookups).filter(x => x.mode === Mode.Global) as IdNameAndNumber[], currentGlobalModule.lookups as IdNameAndNumber[])
        : {existingComponents:[],missingComponents:[]};

    let functions = Object.keys(currentGlobalModule).length > 0 ?
        getComponents(copyArray(reportConfig.userDefinedFunctions).filter(x => x.mode === Mode.Global) as IdNameAndNumber[], currentGlobalModule.userDefinedFunctions as IdNameAndNumber[])
        : {existingComponents:[],missingComponents:[]};

    let variables = Object.keys(currentGlobalModule).length > 0 ?
        getComponents(copyArray(reportConfig.variables).filter(x => x.mode === Mode.Global) as IdNameAndNumber[], currentGlobalModule.variables as IdNameAndNumber[])
        : {existingComponents:[],missingComponents:[]};

    return {
        missingLookups: lookups.missingComponents as Lookup[],
        existingLookups: lookups.existingComponents as NameAndError[],
        missingUserDefinedFunctions: functions.missingComponents as UserDefinedFunction[],
        existingUserDefinedFunctions: functions.existingComponents as NameAndError[],
        missingVariables: variables.missingComponents as ReportVariable[],
        existingVariables: variables.existingComponents as NameAndError[],
        existingLookupsLogics: [],
        existingUdfLogics: [],
        existingVariablesLogics: []
    }
}

const getCurrentGlobalModuleConfig = async () => {

    let response = await httpClient.get<GlobalModuleConfig>('report/global-module');

    return response.data;
}

const createGlobalModule = async (): Promise<GlobalModuleConfig> => {

    let response = await httpClient.post('report/global-module/generate-new', { RefDataSources: [], name: 'Global Module Config', lookups: [], variables: [], UserDefinedFunctions: [] });

    return response.data;
}

const getComponents = (reportConfigComponents: IdNameAndNumber[], globalModuleComponents: IdNameAndNumber[]): MissingAndExistingComponents => {

    let names = reportConfigComponents.map(x => x.name.toLowerCase());

    let existingComponents = [] as NameAndError[];

    let missingComponents = [] as IdNameAndNumber[];

    globalModuleComponents.filter(x => names.includes(x.name.toLowerCase())).forEach((x) => {
        existingComponents.push({ error: '', name: x.name });
    });

    let globalModuleComponentsNames = globalModuleComponents.map(x => x.name.toLowerCase());

    reportConfigComponents.forEach((c) => {
        if (!globalModuleComponentsNames.includes(c.name.toLowerCase())) {
            missingComponents.push(c as IdNameAndNumber);
        }
    })

    return {
        existingComponents,
        missingComponents
    } as MissingAndExistingComponents
}

const getComponentNameExistsError = (component: string, name: string): string => {

    return `A ${component} with name "${name}" already exists.`;
}

const updateReportConfigGlobalComponents = (reportConfig: ReportConfig,
    globalModuleConfig: GlobalModuleConfig,
    resultComponents: ComponentInPopup[]
): void => {

    let lookups = [] as Lookup[];

    reportConfig.lookups.forEach((lookup: Lookup) => {

        if (lookup.mode === Mode.Global) {

            let component = resultComponents.find(x => !x.useUpdateLogic && x.componentType === ComponentType.Lookup && x.name.toLowerCase() === lookup.name.toLowerCase());

            let targetLookup = {} as Lookup;
            if (component) {
                targetLookup = globalModuleConfig.lookups.find(x => x.name.toLowerCase() === component?.newName?.toLowerCase() as string) as Lookup;
            }
            else {
                targetLookup = globalModuleConfig.lookups.find(x => x.name.toLowerCase() === lookup.name.toLowerCase()) as Lookup;
            }

            if (targetLookup) {

                let newLookup = copyObject(targetLookup);

                newLookup.globalLookupModuleId = targetLookup.id as number;
                newLookup.name = component?.newName || lookup.name;
                newLookup.id = null;
                newLookup.number = lookup.number;
                newLookup.mode = Mode.Global;

                lookups.push(newLookup)
            }
            else {
                lookups.push(lookup);
            }
        }
        else {
            lookups.push(lookup);
        }
    });

    reportConfig.lookups = lookups;

    let udfs = [] as UserDefinedFunction[];

    reportConfig.userDefinedFunctions.forEach((udf) => {
        if (udf.mode === Mode.Global) {

            let component = resultComponents.find(x => !x.useUpdateLogic && x.componentType === ComponentType.UserDefinedFunction && x.name.toLowerCase() === udf.name.toLowerCase());

            let targetUdf = {} as UserDefinedFunction;
            if (component) {
                targetUdf = globalModuleConfig.userDefinedFunctions.find(x => x.name.toLowerCase() === component?.newName?.toLowerCase() as string) as UserDefinedFunction;
            }
            else {
                targetUdf = globalModuleConfig.userDefinedFunctions.find(x => x.name.toLowerCase() === udf.name.toLowerCase()) as UserDefinedFunction;
            }

            if (targetUdf) {

                let newUdf = copyObject(targetUdf);

                newUdf.globalUDFModuleId = targetUdf.id as number;
                newUdf.name = component?.newName || udf.name;
                newUdf.id = null;
                newUdf.number = udf.number;
                newUdf.mode = Mode.Global;
                udfs.push(newUdf)
            }
            else {
                udfs.push(udf);
            }
        }
        else {
            udfs.push(udf);
        }
    });

    reportConfig.userDefinedFunctions = udfs;

    let variables = [] as ReportVariable[];

    reportConfig.variables.forEach((v) => {
        if (v.mode === Mode.Global) {

            let component = resultComponents.find(x => !x.useUpdateLogic && x.componentType === ComponentType.Variable && x.name.toLowerCase() === v.name.toLowerCase());

            let targetVariable = {} as ReportVariable;
            if (component) {
                targetVariable = globalModuleConfig.variables.find(x => x.name.toLowerCase() === component?.newName?.toLowerCase() as string) as ReportVariable;
            }
            else {
                targetVariable = globalModuleConfig.variables.find(x => x.name.toLowerCase() === v.name.toLowerCase()) as ReportVariable;
            }

            if (targetVariable) {

                let newVariable = copyObject(targetVariable);

                newVariable.globalVariableModuleId = targetVariable.id as number;
                newVariable.name = component?.newName || v.name;
                newVariable.id = null;
                newVariable.number = v.number;
                newVariable.mode = Mode.Global;

                variables.push(newVariable)
            }
            else {
                variables.push(v);
            }
        }
        else {
            variables.push(v);
        }
    });

    reportConfig.variables = variables;
}

const resetConfigComponentsBeforeGetChanges = (config: ReportConfig): void => {
    config.lookups.forEach((x, index) => { x.number = index + 1; x.mode = Mode.Local; x.id = 0; });
    config.userDefinedFunctions.forEach((x) => { x.mode = Mode.Local; x.id = 0; });
    config.variables.forEach((x) => { x.mode = Mode.Local; x.id = 0; });
}

const getColumns = (onComponentNamed: (name: string, value: string, componentType: ComponentType) => void,
    setComponentUseUpdateLogic: (name: string, componentType: ComponentType, value: boolean) => void,
    onCompareLogicClick: (name: string, componentType: ComponentType) => void): ColumnDefinition<ComponentInPopup>[] => {

    return [
        {
            header: 'Type',
            getContent: x => x.componentType,
            width: '200px'
        },
        {
            header: 'Name',
            getContent: x => x.name,
            width: '250px'
        },
        {
            header: 'Compare',
            getContent: (x, e) => <button className='btn cb-btn' onClick={() => onCompareLogicClick(x.name, x.componentType)}>Differences</button>,
            width: '150px'
        },
        {
            header: 'New name',
            getContent: (x) =>
                <div>
                    <input
                        name={x.name}
                        disabled={!x.showNewNameInput}
                        className='form-control width-300px'
                        type='text'
                        placeholder={x.showNewNameInput ? 'Type here new name' : ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onComponentNamed(x.name, e.currentTarget.value, x.componentType)}
                    />
                    {x.error &&
                        <p className='error'>{x.error}</p>
                    }
                </div>,
            width: '350px'
        },
        {
            header: 'Replace logic',
            getContent: x => <SwitchComponent
                disableLabel='False'
                enableLabel='True'
                labelClasses='default-label'
                checked={x.useUpdateLogic}
                onChange={(value: boolean) => { setComponentUseUpdateLogic(x.name, x.componentType, value); }}
            />,
            width: '200px'
        }
    ];
}


const resolveMissingComponents = (currentGlobalModuleConfig: GlobalModuleConfig, globalModuleComponentsAnalysisResult: GlobalModuleComponentsAnalysisResult, maps: IdMaps): void => {

    let newLookups = copyArray(globalModuleComponentsAnalysisResult.missingLookups);

    newLookups.forEach((l) => {
        l.number = currentGlobalModuleConfig.lookups.length + 1;
        l.globalLookupModuleId = 0
    });

    updateLookupsDataSources(newLookups, maps);

    currentGlobalModuleConfig.lookups = currentGlobalModuleConfig.lookups.concat(newLookups);

    let newUdfs = copyArray(globalModuleComponentsAnalysisResult.missingUserDefinedFunctions);

    newUdfs.forEach((udf) => {
        udf.number = currentGlobalModuleConfig.userDefinedFunctions.length + 1;
    });

    currentGlobalModuleConfig.userDefinedFunctions = currentGlobalModuleConfig.userDefinedFunctions.concat(newUdfs);

    let newVariables = copyArray(globalModuleComponentsAnalysisResult.missingVariables);

    newVariables.forEach((v) => {
        v.number = currentGlobalModuleConfig.variables.length + 1;
    });

    currentGlobalModuleConfig.variables = currentGlobalModuleConfig.variables.concat(newVariables);
}


export {
    updateLookupsDataSources,
    analyseGlobalModuleComponents,
    findUsedGlobalModuleDataSources,
    getCurrentGlobalModuleConfig,
    getComponentNameExistsError,
    resetConfigComponentsBeforeGetChanges,
    updateReportConfigGlobalComponents,
    getColumns,
    resolveMissingComponents,
    createGlobalModule
}